import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { SecurityService } from '../security/security.service';
import { ReviewAuthService } from 'src/app/pages/review/review-auth.service';
import { ReviewLoadingService } from 'src/app/pages/review/shared/services/review-loading.service';
import { isReviewCompletedError } from 'src/app/pages/review/shared/review-completed-error.util';
import { environment } from 'src/environments/environment';

/**
 * Tracks whether a token refresh is currently in progress.
 * Shared across all interceptor invocations to prevent concurrent refreshes.
 */
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Endpoints that should NOT receive an Authorization header
 * and should NOT trigger 401 retry logic.
 */
const PUBLIC_ENDPOINTS = [
  '/login',
  '/loginOrg',
  '/register',
  '/forgotPassword',
  '/activateUser',
  '/resetPassword',
  '/refreshToken',
  '/review/lightauth/request',
  '/review/lightauth/verify',
  '/review/lightauth/refresh',
  // Plan #66: auth nueva — sus 401 son errores de negocio (código erróneo,
  // sesión de alta caducada…), NUNCA deben disparar el refresh de sesión.
  '/verify-otp',
  '/resend-otp',
  '/register/verify',
  '/register/resend',
  '/password-reset/request',
  '/password-reset/validate',
  '/password-reset/confirm',
  '/logout',
];

function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINTS.some(ep => url.endsWith(ep) || url.includes(ep + '?'));
}

/**
 * Determines the current auth mode based on tokens in localStorage.
 * - 'regular': user logged in with email/password (bearerToken)
 * - 'light': external reviewer logged in with OTP (lightBearerToken)
 * - 'apitoken': fallback to API token (restoApiToken)
 * - 'none': no authentication
 */
function getAuthMode(): 'regular' | 'light' | 'apitoken' | 'none' {
  if (localStorage.getItem('bearerToken')) return 'regular';
  if (localStorage.getItem('lightBearerToken')) return 'light';
  if (environment.restoApiToken) return 'apitoken';
  return 'none';
}

/**
 * Adds the appropriate Authorization header to a request.
 * On first pass, doesn't override existing headers (backward compat with api.service.ts).
 * On retry (force=true), always replaces with the fresh token from localStorage.
 */
function addAuthHeader(req: HttpRequest<unknown>, force = false): HttpRequest<unknown> {
  // On first pass, don't override if the request already has an Authorization header
  if (!force && (req.headers.has('Authorization') || req.headers.has('APIToken'))) {
    return req;
  }

  // Strip old auth headers before setting new ones
  let headers = req.headers;
  if (force) {
    headers = headers.delete('Authorization').delete('APIToken');
  }

  const mode = getAuthMode();
  switch (mode) {
    case 'regular':
      return req.clone({
        headers: headers.set('Authorization', `Bearer ${localStorage.getItem('bearerToken')}`)
      });
    case 'light':
      return req.clone({
        headers: headers.set('Authorization', `LightBearer ${localStorage.getItem('lightBearerToken')}`)
      });
    case 'apitoken':
      return req.clone({
        headers: headers.set('APIToken', environment.restoApiToken)
      });
    default:
      return req.clone({ headers });
  }
}

/**
 * HTTP Interceptor that handles:
 * 1. Automatically adding auth headers to all non-public requests
 * 2. Detecting 401 responses and attempting token refresh
 * 3. Queuing concurrent requests during refresh
 * 4. Redirecting to login when refresh fails
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

  // inject() must be called here — inside the interceptor body (injection context).
  // It will throw NG0203 if called inside catchError/switchMap callbacks.
  const securityService = inject(SecurityService);
  const reviewAuthService = inject(ReviewAuthService);
  const reviewLoadingService = inject(ReviewLoadingService);

  // Skip public endpoints entirely
  if (isPublicEndpoint(req.url)) {
    return next(req);
  }

  // Add auth header
  const authReq = addAuthHeader(req);

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handle401Error(securityService, reviewAuthService, reviewLoadingService, req, next);
      }
      if (isReviewCompletedError(error)) {
        reviewLoadingService.loading = false;
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handles 401 errors by attempting to refresh the token.
 * If a refresh is already in progress, queues the request to retry after completion.
 */
function handle401Error(
  securityService: SecurityService,
  reviewAuthService: ReviewAuthService,
  reviewLoadingService: ReviewLoadingService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authMode = getAuthMode();

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshObs = authMode === 'light'
      ? securityService.refreshLightToken()
      : securityService.refreshRegularToken();

    // No user-session token — show lightAuth modal so the reviewer can authenticate,
    // then retry the original request. This handles all review pages automatically
    // without requiring each component to implement its own 401 error handling.
    if (authMode === 'none' || authMode === 'apitoken') {
      isRefreshing = false;
      reviewLoadingService.loading = false;
      return reviewAuthService.showAuthModal().pipe(
        switchMap(() => {
          if (reviewAuthService.hasAuthToken()) {
            return next(addAuthHeader(req, true));
          }
          return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'No auth token' }));
        })
      );
    }

    return refreshObs.pipe(
      switchMap((newToken: string) => {
        isRefreshing = false;
        refreshTokenSubject.next(newToken);
        // Retry the original request with the fresh token (force=true to replace old header)
        return next(addAuthHeader(req, true));
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        // Session is dead — clean up and redirect
        if (authMode === 'light') {
          securityService.handleLightSessionExpired();
        } else {
          securityService.handleSessionExpired();
        }

        return throwError(() => err);
      })
    );
  } else {
    // A refresh is already in progress — wait for it to complete, then retry
    return refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(() => next(addAuthHeader(req, true)))
    );
  }
}
